import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataApiResponse, DataCollectionApiResponse } from "./types";
import apis from "#store/api";
import { RESPONDER_TIME_LOGS_KEY } from "./responderTimeLogs";
import { STATUS_MAP_KEY } from "./statusMap";
import { showToast } from '#root/components/GlobalToaster';

const RESPONDERS_KEY = 'responders';

export interface ResponderBase {
  incidentId: string;

  firstName: string;
  lastName: string;
  nickname: string;
  departmentOrAgency: string;
  email: string;
  phone: string;
  role?: number;

  lastDayWorked?: Date;
  incidentNotificationDate?: Date;
  firstTravelDay?: Date;
  checkInDate?: Date;
  firstDayOfWork?: Date;
  anticipatedLastDayOfWork?: Date;
  checkOutDate?: Date;

  usarTeamNumber?: number;
  locationId?: string;
  status?: number;
  isTeamLeader?: boolean;
}

export interface Responder extends ResponderBase {
  id: string;
  personalImageUrl?: string;
  licenseImageUrl?: string;
  createdDate: Date;
  mealAccess?: string;
  incidentName: string;
  locationName: string;
  totalTime?: string; // Total time in ticks or milliseconds
  totalMileage?: number;
  group?: number;
  isIndividualAssignment?: boolean;
}

export interface GetRespondersParams {
  incidentId?: string;
  name?: string;
  departmentOrAgency?: string;
  usarTeamNumber?: number;
  role?: number;
  status?: number;
  isDeployable?: boolean;
  includeTimeLogs?: boolean;
  startDate?: Date;
  endDate?: Date;
  skip?: number;
  take: number;
}

interface GetResponderByIdParams {
  id: string;
  includeTimeLogs?: boolean;
}

interface CreateResponderParams {
  responder: ResponderBase;
  recaptchaToken?: string;
}

interface UpdateResponderParams {
  responder: {
    id: string;
    status: number;
    checkInDate?: Date;
    firstDayOfWork?: Date;
    anticipatedLastDayOfWork?: Date;
    checkOutDate?: Date;
    locationId?: string;
    group?: number;
    individualAssignment?: boolean;
  };
}

interface DeleteResponderParams {
  id: string;
}

interface BatchDeleteRespondersParams {
  ids: string[];
}

export interface GetResponderTeamParams {  
  incidentId: string;  
  teamNumber: number;  
}  
  
export interface UpdateResponderTeamParams {  
  request: {  
    incidentId: string;  
    usarTeamNumber: number;  
    status: number;  
    locationId?: string;  
    //isIndividualAssignment?: boolean;
  }  
}  

// Fetch responders list
export const useResponders = (params: GetRespondersParams) => {
  return useQuery({
    queryKey: [RESPONDERS_KEY, params],
    queryFn: async () => {
      const response = await apis.responders.getRespondersList({ params });
      return response.data as DataCollectionApiResponse<Responder>;
    },
  });
};

// Fetch responders with infinite loading
export const useInfiniteResponders = (params: Omit<GetRespondersParams, 'skip'>) => {
  return useInfiniteQuery<
    DataCollectionApiResponse<Responder>,
    Error,
    InfiniteData<DataCollectionApiResponse<Responder>>,
    [string, string, GetRespondersParams],
    number
  >({
    queryKey: [RESPONDERS_KEY, "infinite", params],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await apis.responders.getRespondersList({ 
        params: {
          ...params,
          skip: pageParam * params.take
        }
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length < params.take) return undefined;
      return allPages.length;
    }
  });
};

// Fetch responder by id
export const useResponderById = (params: GetResponderByIdParams) => {
  return useQuery({
    queryKey: [RESPONDERS_KEY, params],
    queryFn: async () => {
      const response = await apis.responders.getResponderById({ data: params });
      return response.data as DataApiResponse<Responder>;
    },
  });
};

// Create responder
export const useCreateResponder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateResponderParams) => {
      const response = await apis.responders.createResponder({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESPONDERS_KEY] });
    },
  });
};

// Update responder
export const useUpdateResponder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateResponderParams) => {
      const response = await apis.responders.updateResponder({ data: params });
      return response.data as DataApiResponse<Responder>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESPONDERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [RESPONDER_TIME_LOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STATUS_MAP_KEY] });
    },
  });
};

// Deny responder
export const useDenyResponder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteResponderParams) => {
      const response = await apis.responders.denyResponder({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESPONDERS_KEY] });
    }
  });
};

// Batch delete responders
export const useBatchDeleteResponders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: BatchDeleteRespondersParams) => {
      const response = await apis.responders.batchDeleteResponders({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESPONDERS_KEY] });
    }
  });
};

export const useResponderTeam = (params: GetResponderTeamParams) => {  
  return useQuery({  
    queryKey: [RESPONDERS_KEY, "team", params],  
    queryFn: async () => {  
      const response = await apis.responders.getResponderTeam({ params });  
      return response.data as DataApiResponse<Responder>;
    },  
    enabled: !!params.incidentId && !!params.teamNumber,  
  });  
};  
  
export const useUpdateResponderTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpdateResponderTeamParams) => {
      const response = await apis.responders.updateResponderTeam({ data: params });
      return response.data as DataCollectionApiResponse<Responder>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RESPONDERS_KEY] });
      showToast("Responder team updated successfully", "success");
    },
    onError: () => {
      showToast("Failed to update responder team", "error");
    },
  });
};